import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { 
  Mail, Plus, Edit, Trash2, Eye, Send, Copy,
  FileText, AlertCircle, Check, X, Loader2,
  ChevronDown, Search, Code
} from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogDescription,
} from './ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';

const API = process.env.REACT_APP_BACKEND_URL;

const CATEGORY_CONFIG = {
  change_order: { label: 'Change Order', color: 'bg-orange-500' },
  milestone: { label: 'Milestone', color: 'bg-green-500' },
  approval: { label: 'Approval', color: 'bg-yellow-500' },
  bid_package: { label: 'Bid Package', color: 'bg-blue-500' },
  rfi: { label: 'RFI', color: 'bg-purple-500' },
  contractor: { label: 'Contractor', color: 'bg-teal-500' },
  client: { label: 'Client', color: 'bg-indigo-500' },
  general: { label: 'General', color: 'bg-gray-500' }
};

export function EmailTemplates({ isOpen, onClose, darkMode }) {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [showEditor, setShowEditor] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSendDialog, setShowSendDialog] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  
  // Editor state
  const [editData, setEditData] = useState({
    name: '',
    category: 'general',
    subject: '',
    body_html: '',
    available_variables: []
  });
  const [newVariable, setNewVariable] = useState('');
  
  // Preview state
  const [previewVariables, setPreviewVariables] = useState({});
  const [previewResult, setPreviewResult] = useState(null);
  
  // Send state
  const [sendEmail, setSendEmail] = useState('');
  const [sendVariables, setSendVariables] = useState({});
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (isOpen) {
      fetchTemplates();
    }
  }, [isOpen]);

  const fetchTemplates = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/api/email-templates`);
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
      toast.error('Failed to load email templates');
    } finally {
      setLoading(false);
    }
  };

  const openEditor = (template = null) => {
    if (template) {
      setEditData({
        id: template.id,
        name: template.name,
        category: template.category,
        subject: template.subject,
        body_html: template.body_html,
        available_variables: template.available_variables || []
      });
    } else {
      setEditData({
        name: '',
        category: 'general',
        subject: '',
        body_html: '',
        available_variables: []
      });
    }
    setShowEditor(true);
  };

  const saveTemplate = async () => {
    try {
      if (editData.id) {
        await axios.put(`${API}/api/email-templates/${editData.id}`, editData);
        toast.success('Template updated');
      } else {
        await axios.post(`${API}/api/email-templates`, editData);
        toast.success('Template created');
      }
      setShowEditor(false);
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save template');
    }
  };

  const deleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      await axios.delete(`${API}/api/email-templates/${templateId}`);
      toast.success('Template deleted');
      fetchTemplates();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete template');
    }
  };

  const openPreview = (template) => {
    setSelectedTemplate(template);
    const vars = {};
    (template.available_variables || []).forEach(v => {
      vars[v] = `[${v}]`;
    });
    setPreviewVariables(vars);
    setPreviewResult(null);
    setShowPreview(true);
  };

  const generatePreview = async () => {
    try {
      const response = await axios.post(
        `${API}/api/email-templates/${selectedTemplate.id}/preview`,
        previewVariables
      );
      setPreviewResult(response.data);
    } catch (error) {
      toast.error('Failed to generate preview');
    }
  };

  const openSendDialog = (template) => {
    setSelectedTemplate(template);
    setSendEmail('');
    const vars = {};
    (template.available_variables || []).forEach(v => {
      vars[v] = '';
    });
    setSendVariables(vars);
    setShowSendDialog(true);
  };

  const sendTemplateEmail = async () => {
    if (!sendEmail) {
      toast.error('Please enter an email address');
      return;
    }
    
    try {
      setSending(true);
      await axios.post(`${API}/api/email-templates/${selectedTemplate.id}/send`, {
        to_email: sendEmail,
        variables: sendVariables
      });
      toast.success(`Email sent to ${sendEmail}`);
      setShowSendDialog(false);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const addVariable = () => {
    if (newVariable && !editData.available_variables.includes(newVariable)) {
      setEditData({
        ...editData,
        available_variables: [...editData.available_variables, newVariable.toLowerCase().replace(/\s+/g, '_')]
      });
      setNewVariable('');
    }
  };

  const removeVariable = (varName) => {
    setEditData({
      ...editData,
      available_variables: editData.available_variables.filter(v => v !== varName)
    });
  };

  const copyVariable = (varName) => {
    navigator.clipboard.writeText(`{{${varName}}}`);
    toast.success(`Copied {{${varName}}} to clipboard`);
  };

  const filteredTemplates = templates.filter(t => {
    const matchesSearch = t.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                          t.subject.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = categoryFilter === 'all' || t.category === categoryFilter;
    return matchesSearch && matchesCategory;
  });

  return (
    <>
      {/* Main Templates Dialog */}
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className={`sm:max-w-4xl ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A] text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Mail className="w-5 h-5 text-[#BC5D41]" />
              Email Templates
            </DialogTitle>
            <DialogDescription className={darkMode ? 'text-gray-500' : ''}>
              Manage email templates for common communications
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            {/* Toolbar */}
            <div className="flex items-center gap-3 mb-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-500" />
                <Input
                  placeholder="Search templates..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className={`pl-9 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
                />
              </div>
              <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                <SelectTrigger className={`w-40 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}>
                  <SelectValue placeholder="Category" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Categories</SelectItem>
                  {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                    <SelectItem key={key} value={key}>{val.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Button onClick={() => openEditor(null)} className="bg-[#BC5D41] hover:bg-[#70170F]">
                <Plus className="w-4 h-4 mr-2" />
                New Template
              </Button>
            </div>

            {/* Templates List */}
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-[#BC5D41]" />
              </div>
            ) : filteredTemplates.length === 0 ? (
              <div className={`text-center py-12 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                <Mail className="w-12 h-12 mx-auto mb-3 opacity-30" />
                <p>No templates found</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-[400px] overflow-y-auto">
                {filteredTemplates.map((template) => {
                  const catConfig = CATEGORY_CONFIG[template.category] || CATEGORY_CONFIG.general;
                  return (
                    <div
                      key={template.id}
                      className={`p-4 rounded-lg border ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : 'bg-white border-gray-200'}`}
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 mb-1">
                            <span className={`font-medium ${darkMode ? 'text-white' : ''}`}>
                              {template.name}
                            </span>
                            <Badge className={`${catConfig.color} text-white text-xs`}>
                              {catConfig.label}
                            </Badge>
                            {template.is_system && (
                              <Badge variant="outline" className="text-xs">System</Badge>
                            )}
                          </div>
                          <p className={`text-sm ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                            Subject: {template.subject}
                          </p>
                          {template.available_variables?.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                              {template.available_variables.slice(0, 5).map((v) => (
                                <code
                                  key={v}
                                  className={`text-xs px-1.5 py-0.5 rounded cursor-pointer ${
                                    darkMode ? 'bg-[#2A2A2A] text-[#BC5D41]' : 'bg-gray-100 text-[#70170F]'
                                  }`}
                                  onClick={() => copyVariable(v)}
                                  title="Click to copy"
                                >
                                  {`{{${v}}}`}
                                </code>
                              ))}
                              {template.available_variables.length > 5 && (
                                <span className="text-xs text-gray-500">
                                  +{template.available_variables.length - 5} more
                                </span>
                              )}
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-1">
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openPreview(template)}
                            className={darkMode ? 'text-gray-400 hover:text-white' : ''}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openSendDialog(template)}
                            className={darkMode ? 'text-gray-400 hover:text-white' : ''}
                          >
                            <Send className="w-4 h-4" />
                          </Button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="sm" className={darkMode ? 'text-gray-400' : ''}>
                                <ChevronDown className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                              <DropdownMenuItem onClick={() => openEditor(template)} className={darkMode ? 'text-white' : ''}>
                                <Edit className="w-4 h-4 mr-2" />
                                Edit
                              </DropdownMenuItem>
                              {!template.is_system && (
                                <DropdownMenuItem onClick={() => deleteTemplate(template.id)} className="text-red-500">
                                  <Trash2 className="w-4 h-4 mr-2" />
                                  Delete
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={onClose}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Editor Dialog */}
      <Dialog open={showEditor} onOpenChange={setShowEditor}>
        <DialogContent className={`sm:max-w-3xl ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A] text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>
              {editData.id ? 'Edit Template' : 'New Template'}
            </DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4 max-h-[60vh] overflow-y-auto">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                  Template Name
                </label>
                <Input
                  value={editData.name}
                  onChange={(e) => setEditData({ ...editData, name: e.target.value })}
                  placeholder="e.g., Payment Reminder"
                  className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
                />
              </div>
              <div>
                <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                  Category
                </label>
                <Select value={editData.category} onValueChange={(v) => setEditData({ ...editData, category: v })}>
                  <SelectTrigger className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(CATEGORY_CONFIG).map(([key, val]) => (
                      <SelectItem key={key} value={key}>{val.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                Subject Line
              </label>
              <Input
                value={editData.subject}
                onChange={(e) => setEditData({ ...editData, subject: e.target.value })}
                placeholder="e.g., Change Order {{change_order_number}} - {{project_name}}"
                className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
              />
            </div>

            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                Variables
              </label>
              <div className="flex gap-2 mb-2">
                <Input
                  value={newVariable}
                  onChange={(e) => setNewVariable(e.target.value)}
                  placeholder="Add variable (e.g., client_name)"
                  className={`flex-1 ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
                  onKeyPress={(e) => e.key === 'Enter' && addVariable()}
                />
                <Button onClick={addVariable} variant="outline" size="sm">
                  <Plus className="w-4 h-4" />
                </Button>
              </div>
              <div className="flex flex-wrap gap-1">
                {editData.available_variables.map((v) => (
                  <Badge
                    key={v}
                    variant="secondary"
                    className={`cursor-pointer ${darkMode ? 'bg-[#2A2A2A]' : ''}`}
                  >
                    <Code className="w-3 h-3 mr-1" />
                    {`{{${v}}}`}
                    <button
                      onClick={() => removeVariable(v)}
                      className="ml-1 hover:text-red-500"
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </Badge>
                ))}
              </div>
            </div>

            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                Email Body (HTML)
              </label>
              <Textarea
                value={editData.body_html}
                onChange={(e) => setEditData({ ...editData, body_html: e.target.value })}
                placeholder="Enter HTML content with {{variable}} placeholders..."
                rows={12}
                className={`font-mono text-sm ${darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}`}
              />
              <p className={`text-xs mt-1 ${darkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                Use {'{{variable_name}}'} for dynamic content. Variables will be replaced when sending.
              </p>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditor(false)}>Cancel</Button>
            <Button onClick={saveTemplate} className="bg-[#BC5D41] hover:bg-[#70170F]">
              <Check className="w-4 h-4 mr-2" />
              Save Template
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Preview Dialog */}
      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className={`sm:max-w-3xl ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A] text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Preview: {selectedTemplate?.name}</DialogTitle>
          </DialogHeader>

          <div className="py-4 space-y-4">
            {/* Variable inputs */}
            <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
              <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : ''}`}>
                Fill in variables for preview:
              </h4>
              <div className="grid grid-cols-2 gap-3">
                {selectedTemplate?.available_variables?.map((v) => (
                  <div key={v}>
                    <label className={`text-xs ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {v.replace(/_/g, ' ')}
                    </label>
                    <Input
                      value={previewVariables[v] || ''}
                      onChange={(e) => setPreviewVariables({ ...previewVariables, [v]: e.target.value })}
                      placeholder={`Enter ${v}`}
                      className={`h-8 text-sm ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                    />
                  </div>
                ))}
              </div>
              <Button onClick={generatePreview} className="mt-3 bg-[#BC5D41] hover:bg-[#70170F]" size="sm">
                <Eye className="w-4 h-4 mr-2" />
                Generate Preview
              </Button>
            </div>

            {/* Preview result */}
            {previewResult && (
              <div className={`border rounded-lg overflow-hidden ${darkMode ? 'border-[#2A2A2A]' : ''}`}>
                <div className={`px-4 py-2 text-sm ${darkMode ? 'bg-[#1A1A1A] text-gray-300' : 'bg-gray-100'}`}>
                  <strong>Subject:</strong> {previewResult.subject}
                </div>
                <div
                  className="p-4 bg-white"
                  dangerouslySetInnerHTML={{ __html: previewResult.body_html }}
                />
              </div>
            )}
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowPreview(false)}>Close</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send Dialog */}
      <Dialog open={showSendDialog} onOpenChange={setShowSendDialog}>
        <DialogContent className={`sm:max-w-xl ${darkMode ? 'bg-[#0D0D0D] border-[#1A1A1A] text-white' : ''}`}>
          <DialogHeader>
            <DialogTitle>Send: {selectedTemplate?.name}</DialogTitle>
            <DialogDescription className={darkMode ? 'text-gray-500' : ''}>
              Fill in the details and send this email
            </DialogDescription>
          </DialogHeader>

          <div className="py-4 space-y-4">
            <div>
              <label className={`text-sm font-medium ${darkMode ? 'text-gray-300' : ''}`}>
                Recipient Email *
              </label>
              <Input
                type="email"
                value={sendEmail}
                onChange={(e) => setSendEmail(e.target.value)}
                placeholder="recipient@example.com"
                className={darkMode ? 'bg-[#1A1A1A] border-[#2A2A2A]' : ''}
              />
            </div>

            <div className={`p-4 rounded-lg ${darkMode ? 'bg-[#1A1A1A]' : 'bg-gray-50'}`}>
              <h4 className={`text-sm font-medium mb-3 ${darkMode ? 'text-gray-300' : ''}`}>
                Template Variables
              </h4>
              <div className="space-y-3">
                {selectedTemplate?.available_variables?.map((v) => (
                  <div key={v}>
                    <label className={`text-xs capitalize ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                      {v.replace(/_/g, ' ')}
                    </label>
                    <Input
                      value={sendVariables[v] || ''}
                      onChange={(e) => setSendVariables({ ...sendVariables, [v]: e.target.value })}
                      placeholder={`Enter ${v.replace(/_/g, ' ')}`}
                      className={`h-8 text-sm ${darkMode ? 'bg-[#0D0D0D] border-[#2A2A2A]' : ''}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowSendDialog(false)}>Cancel</Button>
            <Button
              onClick={sendTemplateEmail}
              disabled={sending || !sendEmail}
              className="bg-[#BC5D41] hover:bg-[#70170F]"
            >
              {sending ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4 mr-2" />
                  Send Email
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

export default EmailTemplates;
